import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { SITE, waLink, telLink, WA } from "../config/site";

const LINKS = [
  { label: "Categories", href: "#categories" },
  { label: "Offers", href: "#offers" },
  { label: "Our Store", href: "#store" },
  { label: "Alterations", href: "#alterations" },
  { label: "Brands", href: "#brands" },
  { label: "Delivery", href: "#delivery" },
  { label: "Visit Us", href: "#visit" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
];

export const scrollTo = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -70, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-charcoal/85 backdrop-blur-xl border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[70px] flex items-center justify-between">
        <Logo compact />
        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={(e) => go(e, l.href)}
              className="text-sm text-cream/70 hover:text-gold transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={telLink}
            data-testid="nav-call-button"
            className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-gold border border-white/15 hover:border-gold/50 rounded-full px-4 py-2 transition-colors duration-300"
          >
            <Phone size={14} /> {SITE.phoneDisplay}
          </a>
          <a
            href={waLink(WA.general)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="inline-flex items-center gap-2 text-sm font-semibold bg-gold text-charcoal rounded-full px-5 py-2 hover:bg-gold-hover transition-colors duration-300"
          >
            <MessageCircle size={15} /> WhatsApp
          </a>
        </div>
        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-cream p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-charcoal/95 backdrop-blur-xl"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={(e) => go(e, l.href)}
                  className="text-base text-cream/80 hover:text-gold transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={waLink(WA.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp-button"
                className="mt-2 inline-flex items-center justify-center gap-2 font-semibold bg-gold text-charcoal rounded-full px-5 py-3"
              >
                <MessageCircle size={16} /> Enquire on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
